import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  id: string;
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: (id: string) => void;
}

interface ToastNotification {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ id, message, type = 'info', duration = 5000, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    // Fade in
    setIsVisible(true);
    
    // Auto close after duration
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(id), 300); // Wait for fade out animation
  };

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          container: 'bg-green-50 border-green-200',
          icon: 'text-green-500',
          iconComponent: CheckCircle,
          title: t('common.success')
        };
      case 'error':
        return {
          container: 'bg-red-50 border-red-200',
          icon: 'text-red-500',
          iconComponent: AlertCircle,
          title: t('common.error')
        };
      case 'warning':
        return {
          container: 'bg-yellow-50 border-yellow-200',
          icon: 'text-yellow-500',
          iconComponent: AlertTriangle,
          title: t('common.warning')
        };
      default:
        return {
          container: 'bg-blue-50 border-blue-200',
          icon: 'text-blue-500',
          iconComponent: Info,
          title: t('common.info')
        };
    }
  };

  const { container, icon, iconComponent: IconComponent, title } = getTypeStyles();

  return (
    <div
      className={`transform transition-all duration-300 ease-in-out ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      <div className={`border-l-4 ${container} rounded-lg shadow-lg p-4 max-w-sm`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <IconComponent className={`h-5 w-5 ${icon}`} />
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-gray-900">{title}</p>
            <p className="mt-1 text-sm text-gray-700">{message}</p>
          </div>
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={handleClose}
              className="rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Toast Container Component
export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastNotification[]>([]);

  const addToast = (toast: Omit<ToastNotification, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { ...toast, id }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Make toast functions globally available
  useEffect(() => {
    window.showToast = addToast;
    
    return () => {
      // @ts-ignore
      delete window.showToast;
    };
  }, []);

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          id={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={removeToast}
        />
      ))}
    </div>
  );
};

// Helper functions for easy toast usage
export const showToast = {
  success: (message: string, duration?: number) => {
    // @ts-ignore
    if (typeof window.showToast === 'function') {
      // @ts-ignore
      window.showToast({ message, type: 'success', duration });
    }
  },
  error: (message: string, duration?: number) => {
    // @ts-ignore
    if (typeof window.showToast === 'function') {
      // @ts-ignore
      window.showToast({ message, type: 'error', duration });
    }
  },
  warning: (message: string, duration?: number) => {
    // @ts-ignore
    if (typeof window.showToast === 'function') {
      // @ts-ignore
      window.showToast({ message, type: 'warning', duration });
    }
  },
  info: (message: string, duration?: number) => {
    // @ts-ignore
    if (typeof window.showToast === 'function') {
      // @ts-ignore
      window.showToast({ message, type: 'info', duration });
    }
  }
};

// Declare global type for window.showToast
declare global {
  interface Window {
    showToast?: (toast: Omit<ToastNotification, 'id'>) => void;
  }
}